/**
 * Browser Atrium SDK helper. Tokens come from our server; org keys never leave it.
 */
import { AtriumClient } from '@hawkenai/atrium-sdk';
import { HTTPClient } from './http';

const http = new HTTPClient();
const GEMINI_PREFER = [
	'gemini-3.6-flash',
	'gemini-3.5-flash',
	'gemini-2.5-flash',
	'gemini-flash'
];
const GEMINI_FALLBACK = 'google/gemini-3.6-flash';

let cachedProjectId = null;
let cachedClient = null;

/**
 * Mint a short-lived Atrium session from the HAQ API.
 * @param {string} projectId
 * @returns {Promise<{token: string, expiresIn?: number, apiBase?: string}>}
 */
async function mintAtriumToken(projectId) {
	const payload = await http.robustQuery('/api/atrium/token', { projectId: projectId });
	if (!payload || !payload.token) throw new Error('Atrium session did not return a token');
	return payload;
}

/**
 * Drop the cached AtriumClient so the next call mints again.
 */
export function clearAtriumClientCache() {
	cachedProjectId = null;
	cachedClient = null;
}

/**
 * User-facing text from an Atrium auth or API error.
 * @param {*} error
 * @returns {string}
 */
export function atriumAuthErrorMessage(error) {
	const msg = error && error.message ? String(error.message).trim() : '';
	return msg || 'Failed to authenticate with Atrium';
}

/**
 * AtriumClient for this HAQ project (cached per projectId).
 * @param {string} projectId
 * @returns {Promise<import('@hawkenai/atrium-sdk').AtriumClient>}
 */
export async function getAtriumClient(projectId) {
	if (!projectId) throw new Error('projectId is required');
	if (cachedClient && cachedProjectId === projectId) return cachedClient;
	const first = await mintAtriumToken(projectId);
	const client = new AtriumClient({
		apiBase: first.apiBase || undefined,
		getToken: function() {
			return mintAtriumToken(projectId);
		}
	});
	cachedProjectId = projectId;
	cachedClient = client;
	return client;
}

/**
 * Pick a Gemini chat model from Atrium llm-specs.
 * @param {import('@hawkenai/atrium-sdk').AtriumClient} atrium
 * @returns {Promise<string>}
 */
export async function resolveGeminiModel(atrium) {
	try {
		const specs = await atrium.getLlmSpecs();
		const list = (specs && specs.results) || [];
		for (let p = 0; p < GEMINI_PREFER.length; p++) {
			const needle = GEMINI_PREFER[p];
			const hit = list.find(function(row) {
				const id = String((row && row.id) || '').toLowerCase();
				return id.indexOf(needle) >= 0;
			});
			if (hit && hit.id) return hit.id;
		}
		const anyGemini = list.find(function(row) {
			const id = String((row && row.id) || '').toLowerCase();
			return id.indexOf('gemini') >= 0;
		});
		if (anyGemini && anyGemini.id) return anyGemini.id;
	} catch (error) {
		console.error(error);
	}
	return GEMINI_FALLBACK;
}

/**
 * Sidebar/transcript shape from an Atrium conversation row.
 * @param {object} row
 * @returns {object}
 */
export function mapConversation(row) {
	if (!row) return null;
	return {
		objectId: row.id || row.objectId,
		title: row.title || '',
		createdAt: row.createdAt,
		updatedAt: row.updatedAt,
		model: row.model || null
	};
}

/**
 * Transcript row from an Atrium message.
 * @param {object} row
 * @returns {object}
 */
export function mapMessage(row) {
	if (!row) return null;
	const id = row.id || row.objectId;
	return {
		objectId: id,
		rowKey: id,
		senderType: row.senderType === 'USER' ? 'USER' : 'SYSTEM',
		content: row.content || '',
		thinking: '',
		thinkingLive: false,
		thoughtDurationSec: null,
		model: row.model || null,
		createdAt: row.createdAt
	};
}
